import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink, DollarSign, TrendingUp, Users, ShieldCheck, Sparkles, CheckCircle2, BarChart3 } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import JsonLd from "../components/JsonLd";
import { usePageSEO } from "../hooks/usePageSEO";
import { AFFILIATE_URLS, onAffiliateClick } from "../config/affiliate";

const BASE = "https://www.peptidesformedspas.org";
const APPLY_URL = AFFILIATE_URLS.partnerProgram;
const TRACK = (loc) => onAffiliateClick({ partner: "paramount-partners", product: `affiliate-program-${loc}`, url: APPLY_URL });

const REASONS = [
  {
    icon: DollarSign,
    title: "Earn on every referral",
    body: "Get paid for traffic you already drive. Promote a curated catalog of research-grade peptides through one tracked link and earn commission on qualifying purchases.",
  },
  {
    icon: TrendingUp,
    title: "High-intent niche",
    body: "Med spa, longevity, recovery, and wellness audiences convert at premium rates. Multi-unit and provider-tier pricing means meaningful per-order economics — not penny-stack affiliate dust.",
  },
  {
    icon: Users,
    title: "Built for individuals & businesses",
    body: "Whether you're a content creator, clinic owner, gym, podcaster, or wellness brand, the program scales — sole-operator commission rates and corporate-account economics on the same platform.",
  },
  {
    icon: ShieldCheck,
    title: "Compliant by design",
    body: "Every product ships with research-only labeling. Write educational, evidence-led content with no medical claims, disclose your affiliate relationship per FTC guidelines, and stay clean.",
  },
];

const WHO_ITS_FOR = [
  "Med spa and aesthetic-clinic owners",
  "Longevity, wellness, and biohacking content creators",
  "Gym owners and personal trainers with engaged communities",
  "Podcasters and YouTubers in health, fitness, and recovery",
  "Functional-medicine and integrative-wellness practices",
  "Affiliate marketers and SEO publishers in the supplement / longevity space",
];

const HOW_IT_WORKS = [
  { n: "01", title: "Apply", body: "Click 'Become an Affiliate' and create your Paramount Peptides Partner account. Plan ~5 minutes." },
  { n: "02", title: "Get your link", body: "Receive a unique tracked affiliate link and dashboard. Promote on your site, blog, social, email, or anywhere your audience already lives." },
  { n: "03", title: "Earn", body: "Track clicks, conversions, and commissions in real time. Get paid on a recurring schedule for every qualifying referral." },
];

const AffiliateProgramPage = () => {
  usePageSEO();

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${BASE}/affiliate-program#webpage`,
      url: `${BASE}/affiliate-program`,
      name: "Become an Affiliate | Paramount Peptides Partner Program",
      description:
        "Earn commission for you or your company by promoting research-grade peptides through the Paramount Peptides Partner Program. Built for med spas, content creators, gyms, podcasters, and wellness brands.",
      inLanguage: "en-US",
      isPartOf: { "@id": `${BASE}/#website` },
      breadcrumb: { "@id": `${BASE}/affiliate-program#breadcrumb` },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "@id": `${BASE}/affiliate-program#breadcrumb`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${BASE}/` },
        { "@type": "ListItem", position: 2, name: "Affiliate Program", item: `${BASE}/affiliate-program` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "@id": `${BASE}/affiliate-program#howto`,
      name: "How to become a Paramount Peptides affiliate",
      description: "Three-step process to join the Paramount Peptides Partner Program through PeptidesForMedSpas.",
      step: HOW_IT_WORKS.map((s, i) => ({
        "@type": "HowToStep",
        position: i + 1,
        name: s.title,
        text: s.body,
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "@id": `${BASE}/affiliate-program#faq`,
      mainEntity: [
        {
          "@type": "Question",
          name: "Who can join the affiliate program?",
          acceptedAnswer: { "@type": "Answer", text: "Individuals and businesses who can responsibly promote research-grade materials with educational, research-only language. Med spa owners, content creators, gyms, podcasters, wellness brands, and affiliate marketers all qualify. Final approval is at the discretion of Paramount Peptides." }
        },
        {
          "@type": "Question",
          name: "Is there a cost to join?",
          acceptedAnswer: { "@type": "Answer", text: "No — applying and joining is free. You only earn commission on qualifying referred purchases." }
        },
        {
          "@type": "Question",
          name: "How and when do I get paid?",
          acceptedAnswer: { "@type": "Answer", text: "Commission rates, payment schedules, and minimum-payout thresholds are set by Paramount Peptides and are visible inside the partner dashboard. PeptidesForMedSpas does not handle payments." }
        },
        {
          "@type": "Question",
          name: "What can I and can't I say in my marketing?",
          acceptedAnswer: { "@type": "Answer", text: "Use educational, research-only language. Do not make therapeutic or disease-treatment claims. Do not imply FDA approval where none exists. Disclose your affiliate relationship per FTC endorsement guidelines. See our /compliance page for the full posture we recommend mirroring." }
        },
        {
          "@type": "Question",
          name: "Does PeptidesForMedSpas earn anything if I sign up through this page?",
          acceptedAnswer: { "@type": "Answer", text: "Yes — we may receive a referral credit when you create a Paramount Peptides Partner account through our link. This is disclosed inline per FTC guidelines. Your commission rates are not affected by the referral relationship." }
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#fffaf5]" data-testid="affiliate-program-page">
      <Header />
      <JsonLd id="affiliate-jsonld" data={jsonLd} />

      {/* Hero */}
      <section className="pt-32 pb-12 bg-gradient-to-b from-[#fdf2f4] to-[#fffaf5]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-[#e8c5b8] rounded-full mb-5">
            <DollarSign className="w-3.5 h-3.5 text-[#b89a6d]" />
            <span className="text-xs uppercase tracking-wider text-[#b89a6d] font-semibold">
              Affiliate Partner Program
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#3a2a26] mb-6 leading-tight" data-testid="affiliate-h1">
            Become an Affiliate.
            <span className="block bg-gradient-to-r from-[#d4a5ad] to-[#b89a6d] bg-clip-text text-transparent">
              Earn for you or your company.
            </span>
          </h1>
          <p className="text-base md:text-lg text-[#5a4a40] max-w-3xl leading-relaxed mb-8">
            Join the <strong className="text-[#3a2a26]">Paramount Peptides Partner Program</strong> and
            earn commission on every qualifying purchase you refer. Built for med spas, content creators,
            gyms, podcasters, and wellness brands who already speak to high-intent peptide-curious audiences.
            Free to join — get paid for traffic you already drive.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <a
              href={APPLY_URL}
              target="_blank"
              rel="noopener noreferrer nofollow sponsored"
              data-testid="affiliate-hero-apply"
              onClick={TRACK("hero")}
              className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-gradient-to-r from-[#d4a5ad] to-[#c4949c] hover:from-[#c4949c] hover:to-[#b48390] text-white font-semibold rounded-full shadow-lg shadow-[#d4a5ad]/30 transition-all"
            >
              Become an Affiliate
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </a>
            <span className="text-sm text-[#a09186]">
              ~5 minutes · free to join · powered by Paramount Peptides
            </span>
          </div>
        </div>
      </section>

      {/* Why */}
      <section className="py-12 bg-[#fffaf5]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-[#3a2a26] mb-2">Why this program</h2>
          <p className="text-[#7a6b62] mb-8">High-intent niche, real economics, compliant by design.</p>
          <div className="grid md:grid-cols-2 gap-6" data-testid="affiliate-reasons">
            {REASONS.map((r, i) => (
              <article
                key={r.title}
                className="bg-white border border-[#f0d8d4] rounded-2xl p-7"
                data-testid={`affiliate-reason-${i}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 bg-[#fdf2f4] rounded-lg flex items-center justify-center">
                    <r.icon className="w-5 h-5 text-[#b89a6d]" />
                  </div>
                  <h3 className="text-base md:text-lg font-bold text-[#3a2a26]">{r.title}</h3>
                </div>
                <p className="text-sm md:text-base text-[#5a4a40] leading-relaxed">{r.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-12 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-[#3a2a26] mb-8">How it works</h2>
          <div className="grid md:grid-cols-3 gap-5" data-testid="affiliate-how-it-works">
            {HOW_IT_WORKS.map((s, i) => (
              <div
                key={s.n}
                className="bg-[#fffaf5] border border-[#f0d8d4] rounded-2xl p-7"
                data-testid={`affiliate-step-${i}`}
              >
                <div className="text-3xl font-bold bg-gradient-to-r from-[#d4a5ad] to-[#b89a6d] bg-clip-text text-transparent mb-3">
                  {s.n}
                </div>
                <h3 className="text-base md:text-lg font-bold text-[#3a2a26] mb-2">{s.title}</h3>
                <p className="text-sm md:text-base text-[#5a4a40] leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who it's for */}
      <section className="py-12 bg-[#fffaf5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-[#3a2a26] mb-3">Who it's built for</h2>
          <p className="text-[#7a6b62] mb-6">
            One application form. Two account paths. Built to scale from solo creator to corporate program.
          </p>
          <div className="bg-white border border-[#f0d8d4] rounded-2xl p-7" data-testid="affiliate-audience">
            <ul className="grid sm:grid-cols-2 gap-3">
              {WHO_ITS_FOR.map((w) => (
                <li key={w} className="flex items-start gap-2 text-sm md:text-base text-[#5a4a40]">
                  <CheckCircle2 className="w-5 h-5 text-[#b89a6d] mt-0.5 flex-shrink-0" />
                  <span>{w}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Earnings note */}
      <section className="py-12 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#fffaf5] border border-[#f0d8d4] rounded-2xl p-7" data-testid="affiliate-earnings">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 bg-[#fdf2f4] rounded-lg flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-[#b89a6d]" />
              </div>
              <h2 className="text-lg md:text-xl font-bold text-[#3a2a26]">Real economics, not pennies</h2>
            </div>
            <p className="text-sm md:text-base text-[#5a4a40] leading-relaxed mb-3">
              The peptide research catalog is a premium AOV (average order value) niche. Multi-unit
              orders, provider-tier pricing, and a recurring-purchase pattern mean the per-order economics
              are meaningfully higher than typical Amazon Associates or supplement-aisle programs.
            </p>
            <p className="text-sm md:text-base text-[#5a4a40] leading-relaxed">
              Specific commission rates, cookie windows, payout schedules, and bonus tiers are set by
              Paramount Peptides and visible inside the partner dashboard once you're accepted.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-[#fffaf5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#fdf2f4] via-[#faeae5] to-[#f7e0d2] border border-[#e8c5b8] p-10 md:p-14 text-center">
            <Sparkles className="w-7 h-7 text-[#b89a6d] mx-auto mb-4" />
            <h2 className="text-2xl md:text-4xl font-bold text-[#3a2a26] mb-4">
              Start earning today
            </h2>
            <p className="text-base md:text-lg text-[#5a4a40] max-w-2xl mx-auto mb-8 leading-relaxed">
              Free to join. Two-minute application. Get your tracked link and partner dashboard
              immediately after approval.
            </p>
            <a
              href={APPLY_URL}
              target="_blank"
              rel="noopener noreferrer nofollow sponsored"
              data-testid="affiliate-final-apply"
              onClick={TRACK("final-cta")}
              className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-[#d4a5ad] to-[#c4949c] hover:from-[#c4949c] hover:to-[#b48390] text-white font-semibold rounded-full shadow-lg shadow-[#d4a5ad]/30 transition-all"
            >
              Become an Affiliate
              <ExternalLink className="w-4 h-4" />
            </a>
            <p className="mt-4 text-xs text-[#a09186] font-mono">
              partners.paramountpeptides.com/create-account
            </p>
          </div>
        </div>
      </section>

      {/* Disclosure */}
      <section className="py-10 bg-[#fffaf5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#fdf2f4] border border-[#e8c5b8] rounded-xl p-6">
            <p className="text-sm text-[#5a4a40] leading-relaxed mb-3">
              <strong>Affiliate disclosure (FTC).</strong> PeptidesForMedSpas may receive a referral
              credit when you create a Paramount Peptides Partner account through links on this page.
              Commission rates, eligibility, and program terms are determined by Paramount Peptides.
              Your earning potential is not affected by our referral relationship.
            </p>
            <p className="text-sm text-[#5a4a40] leading-relaxed">
              <strong>Compliant marketing.</strong> Affiliates are expected to use research-only,
              educational language and avoid therapeutic, disease-treatment, or aesthetic-outcome
              claims. See our{" "}
              <Link to="/compliance" className="text-[#b89a6d] underline hover:text-[#a08560]" data-testid="affiliate-to-compliance">
                Compliance &amp; Regulatory page
              </Link>{" "}
              for the editorial standards we recommend mirroring in your own marketing.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AffiliateProgramPage;
